import React from 'react';
import {
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Info,
  Loader2,
  X,
} from 'lucide-react';

type NoticeVariant = 'success' | 'danger' | 'info' | 'confirm';

type NoticeModalProps = {
  isOpen: boolean;
  title: string;
  message?: string;
  details?: string[];
  variant?: NoticeVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose: () => void;
};

const variantConfig: Record<NoticeVariant, { icon: React.ReactNode; ring: string; button: string }> = {
  success: {
    icon: <CheckCircle2 className="w-7 h-7 text-emerald-600" />,
    ring: 'bg-emerald-50',
    button: 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20',
  },
  danger: {
    icon: <AlertCircle className="w-7 h-7 text-red-600" />,
    ring: 'bg-red-50',
    button: 'bg-red-600 hover:bg-red-700 shadow-red-600/20',
  },
  info: {
    icon: <Info className="w-7 h-7 text-blue-600" />,
    ring: 'bg-blue-50',
    button: 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20',
  },
  confirm: {
    icon: <HelpCircle className="w-7 h-7 text-blue-600" />,
    ring: 'bg-blue-50',
    button: 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20',
  },
};

export const NoticeModal: React.FC<NoticeModalProps> = ({
  isOpen,
  title,
  message,
  details = [],
  variant = 'info',
  confirmLabel,
  cancelLabel = 'Fechar',
  loading = false,
  onConfirm,
  onCancel,
  onClose,
}) => {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  const config = variantConfig[variant];

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center px-4 transition-all duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={loading ? undefined : onClose} />

      <div
        className={`relative w-full max-w-lg rounded-2xl bg-white shadow-2xl transition-all duration-200 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-3'
        }`}
      >
        <div className="flex items-start gap-4 p-6">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${config.ring}`}>
            {config.icon}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {message && <p className="mt-2 text-sm text-gray-600">{message}</p>}

            {details.length > 0 && (
              <div className="mt-4 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="space-y-2">
                  {details.map((detail, index) => (
                    <p key={`${detail}-${index}`} className="text-xs leading-5 text-gray-700">
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onCancel || onClose}
            disabled={loading}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          {onConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className={`inline-flex items-center gap-2 rounded-xl px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${config.button}`}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {confirmLabel || 'Confirmar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
